/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React from "react";
import { useHistory } from "react-router-dom";
import CustomButton from "./CustomButton";

/**
 * ProHunterUpsellBanner - Premium promotional banner for Pro Hunter subscription
 * 
 * Usage: Place on Job Match Score Landing Page and Insights Page
 * Only shown to Freemium and Sprint Pack users
 * 
 * @param {Object} props
 * @param {string} props.placement - 'job_match' | 'insights' - Determines styling and messaging
 * @param {string} props.userTier - Current user tier: 'freemium' | 'sprint_pack' | 'pro_hunter'
 * @param {Function} props.onDismiss - Optional callback when banner is dismissed
 */
const ProHunterUpsellBanner = ({
  placement = "job_match",
  userTier = "freemium",
  onDismiss = null,
}) => {
  const history = useHistory();

  // Don't show for Pro Hunter users
  if (userTier === "pro_hunter") {
    return null;
  }

  const handleUpgrade = () => {
    history.push("/my-subscriptions");
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    if (onDismiss) {
      onDismiss();
    }
  };

  // Unified styling for both Job Match and Insights placements
  if (placement === "job_match") {
    return (
      <div
        style={{
          background: "#F8F4E6",
          border: "1px solid #F1E9CC",
          borderRadius: "12px",
          width: "calc(100% - 28px)",
          maxWidth: "calc(100% - 28px)",
          boxSizing: "border-box",
          padding: "16px",
          margin: "12px auto 16px",
          position: "relative",
          boxShadow: "0 2px 8px rgba(15, 23, 42, 0.06)",
        }}
      >
        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              color: "#64748B",
              cursor: "pointer",
              fontSize: "18px",
              lineHeight: 1,
              opacity: 0.9,
            }}
          >
            ×
          </button>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            marginBottom: "12px",
          }}
        >
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "7px",
              background: "#F97316",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
              <path
                d="M12 2l3.09 2.26L22 6.27l-5 4.87 1.18 6.88L12 14l-6.18 4.02L7 11.14 2 6.27l6.91-2.01L12 2z"
                fill="#FFFFFF"
                stroke="#FFFFFF"
                strokeWidth="0.4"
              />
            </svg>
          </div>
          <h3
            style={{
              fontSize: "18px",
              fontWeight: "700",
              color: "#0F172A",
              margin: 0,
              lineHeight: 1.2,
            }}
          >
            LAND YOUR DREAM JOB 10× FASTER
          </h3>
        </div>

        {/* Benefits */}
        <div
          style={{
            display: "grid",
            gap: "8px",
            marginBottom: "14px",
          }}
        >
          {[
            "Unlimited Off-Platform Matches",
            "Deep Peer Insight Analysis",
            "AI Resume Rewrites",
          ].map((benefit) => (
            <div
              key={benefit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "13px",
                color: "#334155",
                fontWeight: "600",
              }}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="#F97316"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <CustomButton
          additionalClass="p-3"
          text="Upgrade to Pro"
          backgroundColor="#F97316"
          color="#FFFFFF"
          handleClick={handleUpgrade}
          style={{
            fontWeight: "700",
            fontSize: "14px",
            width: "100%",
            padding: "10px 18px",
            borderRadius: "8px",
            boxShadow: "0 2px 6px rgba(249, 115, 22, 0.28)",
          }}
        />
      </div>
    );
  }

  // Insights placement styling
  if (placement === "insights") {
    return (
      <div
        style={{
          background: "linear-gradient(135deg, #FFD700 0%, #7C3AED 50%, #3A6ED4 100%)",
          borderRadius: "12px",
          width: "calc(100% - 3rem)",
          boxSizing: "border-box",
          padding: "18px 20px",
          margin: "16px 1.5rem 20px",
          position: "relative",
          boxShadow: "0 4px 12px rgba(124, 58, 237, 0.18)",
        }}
      >
        {/* Dismiss button */}
        {onDismiss && (
          <button
            onClick={handleDismiss}
            style={{
              position: "absolute",
              top: "12px",
              right: "12px",
              background: "transparent",
              border: "none",
              color: "#fff",
              cursor: "pointer",
              fontSize: "20px",
              lineHeight: 1,
              opacity: 0.7,
            }}
          >
            ×
          </button>
        )}

        {/* Premium shield icon */}
        <div
          style={{
            position: "absolute",
            top: "14px",
            right: "52px",
            opacity: 0.12,
          }}
        >
          <svg width="56" height="56" fill="none" viewBox="0 0 24 24">
            <path
              d="M12 2l3.09 2.26L22 6.27l-5 4.87 1.18 6.88L12 14l-6.18 4.02L7 11.14 2 6.27l6.91-2.01L12 2z"
              stroke="#fff"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Content */}
        <h3
          style={{
            fontSize: "20px",
            fontWeight: "700",
            color: "#fff",
            margin: "0 0 8px 0",
            textShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          SUPERCHARGE YOUR APPLICATIONS!
        </h3>
        <p
          style={{
            fontSize: "14px",
            color: "rgba(255, 255, 255, 0.95)",
            margin: "0 0 14px 0",
            lineHeight: "1.45",
          }}
        >
          Subscribe to <strong>Pro Hunter</strong> to make your next match and land your dream job 10x faster.
        </p>

        {/* Benefits */}
        <div
          style={{
            display: "flex",
            gap: "14px",
            marginBottom: "16px",
            flexWrap: "wrap",
          }}
        >
          {[
            "Unlimited Off-Platform Matches",
            "Deep Peer Insight Analysis",
            "AI Resume Rewrites",
          ].map((benefit) => (
            <div
              key={benefit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                fontSize: "12px",
                color: "rgba(255, 255, 255, 0.9)",
                fontWeight: "500",
              }}
            >
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24">
                <path
                  d="M20 6L9 17l-5-5"
                  stroke="#FFD700"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>{benefit}</span>
            </div>
          ))}
        </div>

        {/* CTA Button */}
        <CustomButton
          additionalClass="p-3"
          text="Subscribe and Upgrade to Pro"
          backgroundColor="#fff"
          color="#7C3AED"
          handleClick={handleUpgrade}
          style={{
            fontWeight: "700",
            fontSize: "14px",
            padding: "10px 24px",
            borderRadius: "8px",
            boxShadow: "0 3px 6px rgba(0, 0, 0, 0.14)",
          }}
        />
      </div>
    );
  }

  return null;
};

export default ProHunterUpsellBanner;
